'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { Expand } from 'lucide-react';
import type { Media } from '../../../payload-types';
import { cn } from '../../../utilities/classname';
import { MediaElement } from '../media-element';
import type { SubjectsById } from '../subjects-by-id';
import { PortfolioLightbox, type PortfolioLightboxItem } from './portfolio-lightbox';

type PortfolioBrickGridItem = {
  id: string;
  title: string;
  summary: string | null;
  image: Media;
  before: Media | null;
  after: Media | null;
  place: string | null;
  completedLabel: string | null;
  quote: string | null;
  reviewer: string | null;
};

const SINGLE_IMAGE_SIZES = '(min-width: 1600px) 352px, (min-width: 768px) 22vw, 45vw';
const MINIMUM_TILES_PER_GROUP = 12;
const LOOP_DURATION_MS = 54_000;
const LEADING_LOOP_COPIES = 1;

/**
 * Every tile spans two columns. Successive items alternate between the two
 * rows, so the top row starts on odd columns and the bottom row starts on even
 * columns. The final bottom tile overhangs the group by one column; the first
 * bottom tile in the next group begins exactly where it ends.
 */
const tilePlacement = (index: number) => ({
  column: index + 1,
  row: (index % 2) + 1,
});

/**
 * Drive the marquee directly instead of relying on an infinite CSS animation.
 * WebKit can drop infinite CSS animations after navigation or another animation
 * runs on iOS. Measuring the first of the two identical copies also avoids
 * Safari having to resolve a percentage transform against a max-content track.
 */
const useBrickGridLoop = (
  trackRef: React.RefObject<HTMLDivElement | null>,
  firstCopyRef: React.RefObject<HTMLDivElement | null>,
  paused: boolean,
) => {
  const progressRef = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    const firstCopy = firstCopyRef.current;
    if (track === null || firstCopy === null) return;

    const reducedMotion = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    let groupWidth = 0;
    let frame: number | null = null;
    let previousTime: number | null = null;

    const measure = () => {
      const firstBounds = firstCopy.getBoundingClientRect();
      const nextCopy = firstCopy.nextElementSibling;
      groupWidth = nextCopy instanceof HTMLElement
        ? nextCopy.getBoundingClientRect().left - firstBounds.left
        : firstBounds.width;
    };

    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      previousTime = null;
    };

    const animate = (time: number) => {
      if (previousTime !== null && groupWidth > 0) {
        progressRef.current = (progressRef.current + (time - previousTime) / LOOP_DURATION_MS) % 1;
      }

      const offset = (LEADING_LOOP_COPIES + progressRef.current) * groupWidth;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      previousTime = time;
      frame = requestAnimationFrame(animate);
    };

    const syncPlayback = () => {
      stop();

      if (reducedMotion?.matches) {
        progressRef.current = 0;
        track.style.transform = `translate3d(${-LEADING_LOOP_COPIES * groupWidth}px, 0, 0)`;
        return;
      }

      if (!paused && !document.hidden) frame = requestAnimationFrame(animate);
    };

    measure();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resizeObserver?.observe(firstCopy);
    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', syncPlayback);
    reducedMotion?.addEventListener('change', syncPlayback);
    syncPlayback();

    return () => {
      stop();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', syncPlayback);
      reducedMotion?.removeEventListener('change', syncPlayback);
    };
  }, [firstCopyRef, paused, trackRef]);
};

const metaLine = (item: PortfolioBrickGridItem, showPlace: boolean): string =>
  [showPlace ? item.place : null, item.completedLabel].filter(Boolean).join(' · ');

const toLightboxItem = (item: PortfolioBrickGridItem, showPlace: boolean): PortfolioLightboxItem => ({
  id: item.id,
  title: item.title,
  summary: item.summary,
  image: item.image,
  before: item.before,
  after: item.after,
  meta: metaLine(item, showPlace) || null,
  quote: item.quote,
  reviewer: item.reviewer,
});

/**
 * An edge-to-edge, two-row brick grid. Three identical groups move as one
 * track, with the middle copy initially visible, then wrap by exactly one group
 * width for a seamless continuous loop.
 * Short portfolios repeat inside each group so the imagery always extends
 * beyond the viewport.
 */
export const PortfolioBrickGrid: React.FC<{
  items: PortfolioBrickGridItem[];
  header: React.ReactNode;
  containerClassName: string;
  showQuotes?: boolean;
  showPlace?: boolean;
  /** The caller's "View all our work" link (`PortfolioIndexViewAllLink`), placed under the gallery. */
  viewAll?: React.ReactNode;
  /**
   * The Subjects from the server caller (`portfolioSubjects`): each item's by
   * item id, spread on its tile and in the lightbox, and each photo's by Media id.
   */
  subjects?: SubjectsById;
}> = ({ items, header, containerClassName, showQuotes = true, showPlace = true, viewAll, subjects }) => {
  const dialogId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const firstCopyRef = useRef<HTMLDivElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [pausedByPointer, setPausedByPointer] = useState(false);

  useBrickGridLoop(trackRef, firstCopyRef, openIndex !== null || pausedByPointer);

  if (items.length === 0) return null;

  const displayItemCount = Math.ceil(Math.max(MINIMUM_TILES_PER_GROUP, items.length) / 6) * 6;
  const displayItems = Array.from({ length: displayItemCount }, (_, displayIndex) => {
    const sourceIndex = displayIndex % items.length;
    return {
      item: items[sourceIndex],
      repeatedForFill: displayIndex >= items.length,
      sourceIndex,
    };
  });

  return (
    <>
      {/* No heading is `null`: no wrapper, so no `mb-10` of nothing above the gallery. */}
      {header === null || header === undefined ? null : <div className={cn(containerClassName, 'mb-10')}>{header}</div>}
      <div
        className="portfolio-brick-grid-loop"
        role="region"
        aria-label="Portfolio gallery"
        onPointerEnter={(event) => {
          if (event.pointerType !== 'touch') setPausedByPointer(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'touch') setPausedByPointer(false);
        }}
      >
        <div ref={trackRef} className="portfolio-brick-grid-track">
          {[0, 1, 2].map((copyIndex) => (
            <div
              ref={copyIndex === 0 ? firstCopyRef : undefined}
              key={copyIndex}
              data-loop-copy={copyIndex}
              aria-hidden={copyIndex === LEADING_LOOP_COPIES ? undefined : true}
              style={{
                gridTemplateColumns: `repeat(${displayItemCount}, var(--portfolio-brick-grid-column))`,
                marginRight: 'calc(0px - var(--portfolio-brick-grid-column))',
              }}
              className="portfolio-brick-grid-copy grid w-max flex-none gap-0"
            >
              {displayItems.map(({ item, repeatedForFill, sourceIndex }, displayIndex) => {
                const placement = tilePlacement(displayIndex);
                const meta = metaLine(item, showPlace);
                const decorativeCopy = copyIndex !== LEADING_LOOP_COPIES || repeatedForFill;

                return (
                  <figure
                    key={`${item.id}-${displayIndex}`}
                    data-brick-grid-item
                    data-source-index={sourceIndex}
                    aria-hidden={repeatedForFill ? true : undefined}
                    style={{
                      gridColumn: `${placement.column} / span 2`,
                      gridRow: placement.row,
                    }}
                    className="group relative col-span-2 h-full w-full overflow-hidden bg-base-300"
                    {...subjects?.[item.id]}
                  >
                    <MediaElement
                      data={item.image}
                      fill
                      sizes={SINGLE_IMAGE_SIZES}
                      className="transition-transform duration-500 ease-out motion-safe:group-hover:scale-103"
                      subject={subjects?.[item.image.id]}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none" />
                    <button
                      type="button"
                      aria-label={`Expand photo: ${item.title}`}
                      aria-haspopup="dialog"
                      aria-controls={dialogId}
                      tabIndex={decorativeCopy ? -1 : undefined}
                      onClick={() => setOpenIndex(sourceIndex)}
                      className="absolute inset-0 z-20 cursor-zoom-in rounded-box focus-visible:outline-2 focus-visible:-outline-offset-3 focus-visible:outline-white"
                    >
                      <span className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full bg-black/55 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none">
                        <Expand className="size-4" />
                      </span>
                    </button>
                    <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col gap-1.5 p-3 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none sm:p-4 lg:p-5">
                      {meta ? <p className="text-xs font-semibold uppercase tracking-widest text-white/75">{meta}</p> : null}
                      <h3
                        className="text-lg font-semibold leading-snug text-balance lg:text-xl"
                      >
                        {item.title}
                      </h3>
                      {item.summary ? (
                        <p className="line-clamp-2 text-sm leading-relaxed text-white/80">
                          {item.summary}
                        </p>
                      ) : null}
                      {showQuotes && item.quote ? (
                        <blockquote
                          className="border-t border-white/20 pt-2"
                        >
                          <p className="line-clamp-2 text-sm italic leading-relaxed text-white/85">&ldquo;{item.quote}&rdquo;</p>
                          {item.reviewer ? <footer className="mt-1 text-xs text-white/65">{item.reviewer}</footer> : null}
                        </blockquote>
                      ) : null}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          ))}
        </div>
        <span aria-hidden="true" className="portfolio-brick-grid-edge portfolio-brick-grid-edge--start" />
        <span aria-hidden="true" className="portfolio-brick-grid-edge portfolio-brick-grid-edge--end" />
      </div>

      {/*
        The caller's "View all our work" link, back in the page container under
        the edge-to-edge gallery. It carries its own top margin and renders
        nothing while the portfolio index is off.
      */}
      {viewAll === undefined ? null : <div className={containerClassName}>{viewAll}</div>}

      <PortfolioLightbox
        id={dialogId}
        items={items.map((item) => toLightboxItem(item, showPlace))}
        openIndex={openIndex}
        onOpenIndexChange={setOpenIndex}
        showQuotes={showQuotes}
        subjects={subjects}
      />
    </>
  );
};
